/**
 * AREA DETAIL COMPONENT
 * 
 * Full editing view for a single area including:
 * - Room dimensions
 * - All fixture forms for this area type
 */

import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { getAreaType } from '../../constants/areaTypes';
import { getFixtureCategoriesForAreaType, getEmptyFixtureState } from '../../constants/fixtureDefinitions';
import RoomDimensions from './RoomDimensions';
import FixtureForm from './FixtureForm';

const AreaDetail = ({ 
  area, 
  onBack, 
  onUpdateDimensions, 
  onUpdateFixture,
  onUpdateNotes,
  disabled = false 
}) => {
  const areaConfig = getAreaType(area.type);
  const fixtureCategories = getFixtureCategoriesForAreaType(area.type);
  
  // Initialize fixtures if not present
  const fixtures = area.fixtures || getEmptyFixtureState(area.type);

  const handleDimensionChange = (newDimensions) => {
    onUpdateDimensions(area.area_id, newDimensions);
  };

  const handleFixtureChange = (fixtureKey, value) => {
    onUpdateFixture(area.area_id, fixtureKey, value);
  };

  // Check if area type uses standard dimensions (not exterior/roof)
  const showDimensions = !['exterior', 'roof'].includes(area.type);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={onBack}
          className="p-2 text-slate-400 hover:text-slate-200 hover:bg-slate-800 rounded"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex items-center gap-3">
          <span className="text-3xl">{areaConfig.icon}</span>
          <div>
            <h3 className="text-lg font-semibold text-slate-200">{area.label}</h3>
            <p className="text-sm text-slate-500">{areaConfig.label}</p>
          </div>
        </div>
      </div>

      {/* Dimensions */}
      {showDimensions && (
        <RoomDimensions
          dimensions={area.dimensions || {}}
          onChange={handleDimensionChange}
          disabled={disabled}
        />
      )}

      {/* Fixtures by Category */}
      {Object.entries(fixtureCategories).map(([category, categoryFixtures]) => (
        <div key={category} className="space-y-3">
          <h4 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider border-b border-slate-700 pb-2">
            {category}
          </h4>
          <div className="space-y-2">
            {categoryFixtures.map(fixture => (
              <FixtureForm
                key={fixture.key}
                fixture={fixture}
                value={fixtures[fixture.key] || { condition: 'na', details: {}, notes: '' }}
                onChange={(value) => handleFixtureChange(fixture.key, value)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Area Notes */}
      <div className="space-y-2">
        <h4 className="text-sm font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-700 pb-2">
          Area Notes
        </h4>
        <textarea
          value={area.notes || ''}
          onChange={(e) => onUpdateNotes(area.area_id, e.target.value)}
          disabled={disabled}
          rows={3}
          placeholder="Any additional notes about this area..."
          className="w-full px-3 py-2 bg-slate-950 border border-slate-700 text-slate-100 rounded-sm
            focus:border-emerald-500 focus:outline-none disabled:opacity-50 resize-none"
        />
      </div>
    </div>
  );
};

export default AreaDetail;
